import React, { useState, useEffect } from 'react';
import { getWaConfigs, createWaConfig, testWaConfig, deleteWaConfig } from '../api';
import toast from 'react-hot-toast';
import { MessageSquare, Plus, Trash2, X, CheckCircle, Wifi, Phone } from 'lucide-react';

const PROVIDERS = {
  meta_cloud: { label: 'Meta Cloud API (Official)', help: 'Get credentials from developers.facebook.com → WhatsApp → API Setup' },
  wati: { label: 'WATI', help: 'Get API key from app.wati.io' },
  aisensy: { label: 'AISensy', help: 'Get API key from aisensy.com dashboard' },
  interakt: { label: 'Interakt', help: 'Get API key from interakt.shop' },
  custom: { label: 'Custom API', help: 'Enter your own API endpoint' },
};

export default function WhatsAppSettings() {
  const [configs, setConfigs] = useState([]);
  const [show, setShow] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [f, setF] = useState({ name:'', provider:'meta_cloud', phoneNumberId:'', businessAccountId:'', accessToken:'', apiUrl:'', apiKey:'', phoneNumber:'', displayName:'', webhookVerifyToken:'', isDefault:false });

  useEffect(() => { load(); }, []);
  const load = async () => { try { setConfigs((await getWaConfigs()).data); } catch(e){} };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await testWaConfig(f);
      setTestResult(r.data);
    } catch(e) { setTestResult({ success:false, message: e.response?.data?.message||'Test failed' }); }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!f.name || !f.accessToken || !f.phoneNumber) return toast.error('Name, token, and phone number required');
    if (f.provider === 'meta_cloud' && !f.phoneNumberId) return toast.error('Phone Number ID required for Meta Cloud API');
    try {
      await createWaConfig(f);
      toast.success('WhatsApp config saved!');
      setShow(false); load();
    } catch(e) { toast.error(e.response?.data?.error||'Failed'); }
  };

  return (<>
    <div className="pg-hdr">
      <h2>WhatsApp Settings</h2>
      <p>Configure WhatsApp Business API for messaging campaigns</p>
      <div className="pg-acts"><button className="btn btn-p" onClick={()=>{setShow(true);setTestResult(null);}}><Plus size={15}/> Add WhatsApp Config</button></div>
    </div>
    <div className="pg-body">
      {configs.length===0 ? <div className="card"><div className="empty"><MessageSquare size={44}/><h3>No WhatsApp configured</h3><p>Add your WhatsApp Business API credentials</p></div></div> :
      configs.map(c=><div key={c._id} className="card" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <div className="stat-icon si-r"><Phone size={17}/></div>
          <div>
            <div style={{fontWeight:600,fontSize:13.5}}>{c.name} {c.isDefault&&<span className="badge b-delivered">Default</span>} <span style={{fontSize:10,color:'var(--txtm)'}}>{c.provider}</span></div>
            <div style={{fontSize:11.5,color:'var(--txtm)',fontFamily:'var(--mono)'}}>{c.phoneNumber} • {c.displayName||''}</div>
          </div>
        </div>
        <button className="btn btn-d btn-sm" onClick={async()=>{await deleteWaConfig(c._id);toast.success('Deleted');load();}}>Delete</button>
      </div>)}

      <div className="card" style={{marginTop:20}}>
        <div className="card-title" style={{marginBottom:8}}>Setup Guide</div>
        <p style={{fontSize:12,color:'var(--txt2)',lineHeight:2}}>
          <strong>Meta Cloud API (Official):</strong><br/>
          1. Go to developers.facebook.com → Create App → Business type<br/>
          2. Add WhatsApp product → API Setup<br/>
          3. Copy: Phone Number ID, Business Account ID, Access Token<br/>
          4. Set webhook URL to: <code style={{background:'var(--bg3)',padding:'1px 5px',borderRadius:4}}>https://yourdomain.com/api/whatsapp/webhook</code><br/><br/>
          <strong>Third-party (WATI/AISensy/Interakt):</strong> Get API key from their dashboard and enter below.
        </p>
      </div>
    </div>

    {show&&<div className="modal-bg" onClick={()=>setShow(false)}><div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
      <div className="modal-hdr"><h3>Add WhatsApp Configuration</h3><button className="btn btn-g btn-sm" onClick={()=>setShow(false)}><X size={15}/></button></div>
      <div className="modal-body">
        <div className="fg"><label className="lbl">Provider</label>
          <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>{Object.entries(PROVIDERS).map(([k,v])=>(
            <button key={k} className={`btn btn-sm ${f.provider===k?'btn-p':'btn-s'}`} onClick={()=>setF({...f,provider:k})}>{v.label}</button>
          ))}</div>
          <div style={{fontSize:10.5,color:'var(--txtm)',marginTop:4}}>{PROVIDERS[f.provider]?.help}</div>
        </div>
        <div className="fg"><label className="lbl">Config Name *</label><input className="inp" value={f.name} onChange={e=>setF({...f,name:e.target.value})} placeholder="My WhatsApp Business"/></div>
        <div className="fr2">
          <div className="fg"><label className="lbl">WhatsApp Phone Number *</label><input className="inp" value={f.phoneNumber} onChange={e=>setF({...f,phoneNumber:e.target.value})} placeholder="+919876543210"/></div>
          <div className="fg"><label className="lbl">Display Name</label><input className="inp" value={f.displayName} onChange={e=>setF({...f,displayName:e.target.value})} placeholder="My Business"/></div>
        </div>

        {f.provider==='meta_cloud'&&<>
          <div className="fr2">
            <div className="fg"><label className="lbl">Phone Number ID *</label><input className="inp" value={f.phoneNumberId} onChange={e=>setF({...f,phoneNumberId:e.target.value})} placeholder="From Meta API Setup page"/></div>
            <div className="fg"><label className="lbl">Business Account ID</label><input className="inp" value={f.businessAccountId} onChange={e=>setF({...f,businessAccountId:e.target.value})} placeholder="Optional"/></div>
          </div>
          <div className="fg"><label className="lbl">Permanent Access Token *</label><input className="inp" value={f.accessToken} onChange={e=>setF({...f,accessToken:e.target.value})} placeholder="EAAxxxxx..."/><div style={{fontSize:10,color:'var(--txtm)',marginTop:3}}>Generate a permanent token via System User in Business Settings</div></div>
        </>}

        {(f.provider==='wati'||f.provider==='aisensy'||f.provider==='interakt'||f.provider==='custom')&&<>
          <div className="fg"><label className="lbl">API URL</label><input className="inp" value={f.apiUrl} onChange={e=>setF({...f,apiUrl:e.target.value})} placeholder="https://api.provider.com/v1/messages"/></div>
          <div className="fg"><label className="lbl">API Key / Access Token *</label><input className="inp" value={f.accessToken} onChange={e=>setF({...f,accessToken:e.target.value})} placeholder="Your API key"/></div>
        </>}

        {testResult&&<div style={{marginTop:8,padding:12,background:testResult.success?'var(--okD)':'var(--errD)',borderRadius:8,border:`1px solid ${testResult.success?'rgba(0,214,143,.2)':'rgba(255,107,107,.2)'}`}}>
          <div style={{fontSize:12,fontWeight:600,color:testResult.success?'var(--ok)':'var(--err)'}}>{testResult.success?'✓ Connected':'✗ Failed'}</div>
          <div style={{fontSize:11,color:'var(--txt2)',marginTop:2}}>{testResult.message}</div>
        </div>}
      </div>
      <div className="modal-ft">
        <button className="btn btn-s" onClick={handleTest} disabled={testing}><Wifi size={15}/> {testing?'Testing...':'Test Connection'}</button>
        <button className="btn btn-p" onClick={handleSave}><CheckCircle size={15}/> Save</button>
      </div>
    </div></div>}
  </>);
}
